import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { BarChart3, Download, Calendar, TrendingUp, Users, DollarSign, Printer, AlertCircle, Info } from 'lucide-react';
import { reportsService } from '../lib/services';
import { format, subDays, startOfMonth, endOfMonth, startOfToday, subMonths } from 'date-fns';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface ReportStats {
  appointments: Record<string, number>;
  revenue: {
    collected: number;
    invoiced: number;
    pending: number;
    overdue: number;
    invoiceCount: number;
    byDepartment: Record<string, number>;
    byPaymentMethod: Record<string, number>;
    dailySeries: Array<{ date: string; amount: number }>;
  };
}

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ReportStats | null>(null);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [datePreset, setDatePreset] = useState('30d');
  const [showDefinitions, setShowDefinitions] = useState(false);

  useEffect(() => {
    loadStats();
  }, [startDate, endDate]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await reportsService.getStats(startDate, endDate);
      setStats(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const applyDatePreset = (preset: string) => {
    setDatePreset(preset);
    const today = new Date();
    
    switch (preset) {
      case 'today':
        setStartDate(format(today, 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      case '7d':
        setStartDate(format(subDays(today, 7), 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      case '30d':
        setStartDate(format(subDays(today, 30), 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      case 'this_month':
        setStartDate(format(startOfMonth(today), 'yyyy-MM-dd'));
        setEndDate(format(endOfMonth(today), 'yyyy-MM-dd'));
        break;
      case 'last_month':
        const lastMonth = subMonths(today, 1);
        setStartDate(format(startOfMonth(lastMonth), 'yyyy-MM-dd'));
        setEndDate(format(endOfMonth(lastMonth), 'yyyy-MM-dd'));
        break;
      case 'custom':
        break;
    }
  };

  const handleExport = () => {
    if (!stats) return;
    
    const csvContent = [
      'Report Type,Value,Count',
      `Collected Revenue,$${stats.revenue.collected.toFixed(2)},${stats.revenue.invoiceCount}`,
      `Invoiced Revenue,$${stats.revenue.invoiced.toFixed(2)},${stats.revenue.invoiceCount}`,
      `Pending Revenue,$${stats.revenue.pending.toFixed(2)},-`,
      `Overdue Revenue,$${stats.revenue.overdue.toFixed(2)},-`,
      '',
      'Department,Revenue',
      ...Object.entries(stats.revenue.byDepartment).map(([dept, amount]) => 
        `${dept},$${amount.toFixed(2)}`
      ),
      '',
      'Payment Method,Amount',
      ...Object.entries(stats.revenue.byPaymentMethod).map(([method, amount]) => 
        `${method},$${amount.toFixed(2)}`
      ),
      '',
      'Appointment Status,Count',
      ...Object.entries(stats.appointments).map(([status, count]) => 
        `${status},${count}`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `medical-report-${startDate}-to-${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  const handlePrint = () => {
    navigate('/reports/print', { 
      state: { 
        stats, 
        startDate, 
        endDate 
      } 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Financial Reports</h1>
            <p className="text-primary-100">Comprehensive revenue and appointment analytics</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowDefinitions(!showDefinitions)}
              className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-all duration-200"
            >
              <Info className="w-4 h-4" />
              <span>Definitions</span>
            </button>
            <button
              onClick={handleExport}
              className="flex items-center space-x-2 bg-success-600 text-white px-4 py-2 rounded-lg hover:bg-success-700 transition-colors duration-200 shadow-md"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 bg-accent-600 text-white px-4 py-2 rounded-lg hover:bg-accent-700 transition-colors duration-200 shadow-md"
            >
              <Printer className="w-4 h-4" />
              <span>Print Report</span>
            </button>
          </div>
        </div>
      </div>

      {/* Definitions Panel */}
      {showDefinitions && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-3">
              <h3 className="font-semibold text-blue-900">How are these numbers calculated?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
                <div>
                  <strong>Collected Revenue:</strong> Sum of all payments actually received in the date range
                </div>
                <div>
                  <strong>Invoiced Revenue:</strong> Total amount of all invoices issued in the date range
                </div>
                <div>
                  <strong>Pending Revenue:</strong> Outstanding balance on sent/partial/overdue invoices (invoice total - payments received)
                </div>
                <div>
                  <strong>Overdue Revenue:</strong> Outstanding balance on invoices past their due date
                </div>
                <div>
                  <strong>Average Invoice:</strong> Total invoiced amount divided by number of invoices
                </div>
                <div>
                  <strong>Collection Rate:</strong> (Collected / Invoiced) × 100%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Range Filter with Presets */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-primary-600" />
              <h3 className="font-semibold text-gray-900">Date Range</h3>
            </div>
            <div className="flex items-center space-x-2">
              {['today', '7d', '30d', 'this_month', 'last_month', 'custom'].map((preset) => (
                <button
                  key={preset}
                  onClick={() => applyDatePreset(preset)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    datePreset === preset
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {preset === 'today' && 'Today'}
                  {preset === '7d' && 'Last 7 Days'}
                  {preset === '30d' && 'Last 30 Days'}
                  {preset === 'this_month' && 'This Month'}
                  {preset === 'last_month' && 'Last Month'}
                  {preset === 'custom' && 'Custom'}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">From:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setDatePreset('custom');
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">To:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setDatePreset('custom');
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-success-50 to-success-100 rounded-lg shadow-md p-6 border border-success-200 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-success-700">Collected Revenue</p>
                <p className="text-3xl font-bold text-success-900 mt-1">${stats.revenue.collected.toFixed(2)}</p>
                <p className="text-xs text-success-600 mt-1">Actual payments received</p>
              </div>
              <DollarSign className="w-10 h-10 text-success-600" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg shadow-md p-6 border border-primary-200 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-primary-700">Invoiced Revenue</p>
                <p className="text-3xl font-bold text-primary-900 mt-1">${stats.revenue.invoiced.toFixed(2)}</p>
                <p className="text-xs text-primary-600 mt-1">Total invoices issued</p>
              </div>
              <BarChart3 className="w-10 h-10 text-primary-600" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-warning-50 to-warning-100 rounded-lg shadow-md p-6 border border-warning-200 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-warning-700">Pending Revenue</p>
                <p className="text-3xl font-bold text-warning-900 mt-1">${stats.revenue.pending.toFixed(2)}</p>
                <p className="text-xs text-warning-600 mt-1">Outstanding balance</p>
              </div>
              <TrendingUp className="w-10 h-10 text-warning-600" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-error-50 to-error-100 rounded-lg shadow-md p-6 border border-error-200 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-error-700">Overdue Revenue</p>
                <p className="text-3xl font-bold text-error-900 mt-1">${stats.revenue.overdue.toFixed(2)}</p>
                <p className="text-xs text-error-600 mt-1">Past due date</p>
              </div>
              <AlertCircle className="w-10 h-10 text-error-600" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-secondary-50 to-secondary-100 rounded-lg shadow-md p-6 border border-secondary-200 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-700">Total Invoices</p>
                <p className="text-3xl font-bold text-secondary-900 mt-1">{stats.revenue.invoiceCount}</p>
                <p className="text-xs text-secondary-600 mt-1">Invoices issued</p>
              </div>
              <BarChart3 className="w-10 h-10 text-secondary-600" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-accent-50 to-accent-100 rounded-lg shadow-md p-6 border border-accent-200 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-accent-700">Average Invoice</p>
                <p className="text-3xl font-bold text-accent-900 mt-1">
                  ${stats.revenue.invoiceCount > 0 ? (stats.revenue.invoiced / stats.revenue.invoiceCount).toFixed(2) : '0.00'}
                </p>
                <p className="text-xs text-accent-600 mt-1">Per invoice</p>
              </div>
              <DollarSign className="w-10 h-10 text-accent-600" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg shadow-md p-6 border border-primary-200 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-primary-700">Collection Rate</p>
                <p className="text-3xl font-bold text-primary-900 mt-1">
                  {stats.revenue.invoiced > 0 ? ((stats.revenue.collected / stats.revenue.invoiced) * 100).toFixed(1) : '0.0'}%
                </p>
                <p className="text-xs text-primary-600 mt-1">Collected / Invoiced</p>
              </div>
              <TrendingUp className="w-10 h-10 text-primary-600" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-secondary-50 to-secondary-100 rounded-lg shadow-md p-6 border border-secondary-200 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-700">Total Appointments</p>
                <p className="text-3xl font-bold text-secondary-900 mt-1">
                  {Object.values(stats.appointments).reduce((sum, count) => sum + count, 0)}
                </p>
                <p className="text-xs text-secondary-600 mt-1">All statuses</p>
              </div>
              <Users className="w-10 h-10 text-secondary-600" />
            </div>
          </div>
        </div>
      )}

      {/* Revenue Breakdown */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-primary-600" />
              Revenue by Department
            </h2>
            <div className="space-y-3">
              {Object.entries(stats.revenue.byDepartment).map(([dept, amount]) => (
                <div key={dept} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 capitalize">{dept}</span>
                  <span className="text-lg font-bold text-primary-900">${amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-success-600" />
              Revenue by Payment Method
            </h2>
            <div className="space-y-3">
              {Object.entries(stats.revenue.byPaymentMethod).map(([method, amount]) => (
                <div key={method} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 capitalize">{method}</span>
                  <span className="text-lg font-bold text-success-900">${amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Appointment Status Breakdown */}
      {stats && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-secondary-600" />
            Appointment Status Breakdown
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(stats.appointments).map(([status, count]) => (
              <div key={status} className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-200">
                <p className="text-3xl font-bold text-gray-900">{count}</p>
                <p className="text-sm text-gray-600 capitalize mt-1">{status}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
